import React, { createContext, useEffect, useContext, useCallback } from "react";

const GoogleTranslateContext = createContext();

const LANGUAGE_CODES = {
  English: "en",
  Bengali: "bn",
  Hindi:   "hi",
  Tamil:   "ta",
};

// ─── Cookie helpers ────────────────────────────────────────────────────────────
const setGoogTransCookie = (langCode) => {
  const value = `/en/${langCode}`;
  document.cookie = `googtrans=${value}; path=/`;
  document.cookie = `googtrans=${value}; domain=${window.location.hostname}; path=/`;
};

const clearGoogTransCookie = () => {
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 UTC";
  document.cookie = `googtrans=; path=/; ${expired}`;
  document.cookie = `googtrans=; domain=${window.location.hostname}; path=/; ${expired}`;
};

// ─── On import: restore the cookie from localStorage so GT initialises in the
//     correct language instead of always defaulting to English on reload. ───────
const restoreLanguageCookie = () => {
  try {
    const saved = localStorage.getItem("appSettings");
    if (saved) {
      const { language } = JSON.parse(saved);
      const code = LANGUAGE_CODES[language];
      if (code && code !== "en") {
        setGoogTransCookie(code);
        return;
      }
    }
  } catch (_) {}
  clearGoogTransCookie(); // default to English
};

restoreLanguageCookie(); // runs synchronously on import (before GT script loads)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Find the hidden Google Translate <select> and trigger a language change.
 * Polls every 100ms for up to 5s while the widget initializes.
 */
const triggerGoogleTranslate = (langCode) => {
  const maxAttempts = 50;
  let attempts = 0;

  const tryChange = () => {
    const select = document.querySelector("select.goog-te-combo");
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }
    attempts++;
    if (attempts < maxAttempts) setTimeout(tryChange, 100);
  };

  tryChange();
};

/** Called from the Filter By dropdown when user picks a language. */
export const applyGoogleTranslateLanguage = (language) => {
  const code = LANGUAGE_CODES[language] || "en";

  // Persist the cookie so the choice survives a reload
  if (code === "en") {
    clearGoogTransCookie();
  } else {
    setGoogTransCookie(code);
  }

  triggerGoogleTranslate(code);
};

export const GoogleTranslateProvider = ({ children }) => {
  const initGoogleTranslate = useCallback(() => {
    if (window.google && window.google.translate) {
      try {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: "en,hi,bn,ta",
            autoDisplay: false,
          },
          "gt_hidden_container"
        );
      } catch (e) {
        // already initialized
      }
    }
  }, []);

  useEffect(() => {
    window.googleTranslateElementInit = initGoogleTranslate;

    if (!document.getElementById("gt-script")) {
      const script = document.createElement("script");
      script.id = "gt-script";
      script.src =
        "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else {
      initGoogleTranslate();
    }
  }, [initGoogleTranslate]);

  return (
    <GoogleTranslateContext.Provider value={{}}>
      {children}
      <div
        id="gt_hidden_container"
        style={{
          position: "absolute",
          visibility: "hidden",
          height: 0,
          overflow: "hidden",
        }}
      />
    </GoogleTranslateContext.Provider>
  );
};

export const useGoogleTranslate = () => useContext(GoogleTranslateContext);
